import { Phone, Mail, Calendar, ArrowRight, Clock } from "lucide-react";
import { CONTACT } from "@/lib/data";

const CHANNELS = [
  {
    icon: Phone,
    overline: "Le plus rapide",
    title: "Par téléphone",
    desc: "Appelez-nous directement. Réponse immédiate (du lundi au samedi, hors week-end). Idéal pour une question rapide ou un appel exploratoire.",
    cta: { label: CONTACT.phone, href: CONTACT.phoneHref, internal: false },
    sla: "⚡ Réponse immédiate",
    bg: "bg-cream",
    color: "text-gold",
  },
  {
    icon: Mail,
    overline: "Le plus complet",
    title: "Par formulaire",
    desc: "Décrivez votre projet en détail (typologie, ville, objectifs). Notre équipe revient vers vous sous 24 h ouvrées avec une étude personnalisée.",
    cta: { label: "Remplir le formulaire", href: "#contact-form", internal: true },
    sla: "📨 Réponse sous 24 h",
    bg: "bg-ink text-cream",
    color: "text-gold",
    featured: true,
  },
  {
    icon: Calendar,
    overline: "Le plus humain",
    title: "Sur rendez-vous",
    desc: "Bloquez un créneau de 15 min directement dans notre calendrier. Visioconférence ou appel téléphonique, à votre préférence.",
    cta: { label: "Choisir un créneau", href: CONTACT.calendly, internal: false, external: true },
    sla: "🗓️ Calendly 15 min",
    bg: "bg-sand",
    color: "text-sage",
  },
];

const ContactChannels = () => (
  <section
    className="py-16 lg:py-20 bg-white"
    data-testid="contact-channels"
  >
    <div className="container-tm">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <p className="overline mb-3">Choisissez votre canal</p>
        <h2 className="font-serif font-light text-ink text-3xl md:text-4xl leading-tight">
          Comment souhaitez-vous{" "}
          <em className="text-gold">nous joindre&nbsp;?</em>
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
        {CHANNELS.map((c, i) => {
          const Icon = c.icon;
          const isInternal = c.cta.internal;
          const isExternal = c.cta.external;
          const handleClick = isInternal
            ? (e) => {
                e.preventDefault();
                const el = document.getElementById(c.cta.href.slice(1));
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 90;
                  window.scrollTo({ top, behavior: "smooth" });
                }
              }
            : undefined;

          return (
            <a
              key={i}
              href={c.cta.href}
              onClick={handleClick}
              target={isExternal ? "_blank" : undefined}
              rel={isExternal ? "noopener noreferrer" : undefined}
              data-testid={`channel-${i}`}
              className={`group relative block rounded-3xl p-7 lg:p-8 border border-ink/5 transition-all duration-500 hover:shadow-lift hover:-translate-y-1 ${
                c.bg
              } ${c.featured ? "shadow-soft md:scale-[1.04]" : ""}`}
            >
              {c.featured && (
                <span className="absolute -top-3 right-6 inline-flex items-center gap-1 bg-gold text-ink text-[10px] uppercase tracking-[0.22em] font-semibold rounded-full px-3 py-1 shadow-soft">
                  Recommandé
                </span>
              )}
              <div
                className={`w-12 h-12 rounded-2xl bg-gold/15 ${c.color} flex items-center justify-center mb-5`}
              >
                <Icon size={20} strokeWidth={1.6} />
              </div>
              <p
                className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 ${
                  c.featured ? "text-gold" : "text-ink/45"
                }`}
              >
                {c.overline}
              </p>
              <h3
                className={`font-serif text-2xl mb-3 ${
                  c.featured ? "text-cream" : "text-ink"
                }`}
              >
                {c.title}
              </h3>
              <p
                className={`font-sans text-sm leading-relaxed mb-5 ${
                  c.featured ? "text-cream/75" : "text-ink/70"
                }`}
              >
                {c.desc}
              </p>
              <span
                className={`inline-flex items-center gap-1.5 text-xs font-medium mb-4 ${
                  c.featured ? "text-cream/70" : "text-ink/55"
                }`}
              >
                {c.sla}
              </span>
              <div className="flex items-center justify-between pt-4 border-t border-current/10">
                <span
                  className={`font-semibold text-sm group-hover:gap-3 transition-all duration-300 inline-flex items-center gap-2 ${
                    c.featured ? "text-gold" : "text-gold"
                  }`}
                >
                  {c.cta.label} <ArrowRight size={14} />
                </span>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  </section>
);

export default ContactChannels;
